<template>
  <div class="recharge-template-config">
    <div class="group-container">
      <div class="group-card">
        <h3 class="group-title">{{ formData.controlGroupName || '对照组' }}</h3>
        
        <div class="form-line">
          <FormItem
            label="选择模板"
            prop="templateId"
          >
            <Select
              v-model="formData.controlTemplateId"
              class="template-select"
              placeholder="— 选择策略模板 —"
              filterable
              remote
              clearable
              
              :remote-method="searchTemplateOptions"
              :loading="templateSearchLoading || templateDetailLoading"
              :disabled="disabled"
              @on-change="handleControlTemplateChange"
            >
              <Option
                v-for="item in templateOptions"
                :key="item.value"
                :value="item.value"
                :label="item.label"
              >
                {{ item.label }}
              </Option>
            </Select>
          </FormItem>
          <span class="form-tip">选择后自动填充下方配置</span>
        </div>

        <Tabs v-model="controlPlatform" :animated="false" class="group-tabs">
          <TabPane label="IOS 配置" name="ios" />
          <TabPane label="Android 配置" name="android" />
        </Tabs>

        <div class="section-title">充值面板配置</div>
        <div class="payment-grid">
          <div
            v-for="panel in getControlConfig(controlPlatform).panels"
            :key="'control-' + controlPlatform + '-' + panel.key"
            class="payment-card"
          >
            <div class="payment-title">{{ panel.title }}</div>
            <div class="switch-box">
              <i-switch v-model="panel.thirdPay" :disabled="disabled" />
              <span>三方支付</span>
            </div>

            <div v-show="panel.thirdPay">
              <RadioGroup v-model="panel.payLinkType" class="radio-row">
                <Radio :label="0" :disabled="disabled">聚合</Radio>
                <Radio :label="1" :disabled="disabled">分步</Radio>
              </RadioGroup>

              <div class="table-title">
                <span>支付方式</span>
                <Button
                  size="small"
                  :disabled="disabled || panel.payMethods.length >= maxMethodCount"
                  @click="addPayMethod(panel)"
                >
                  + 新增
                </Button>
              </div>
              <div class="method-table">
                <div class="method-head">
                  <span>序</span>
                  <span>支付方式</span>
                  <span>渠道</span>
                  <span>默认</span>
                  <span></span>
                </div>
                <draggable
                  v-model="panel.payMethods"
                  handle=".drag-handle"
                  :disabled="disabled"
                  :animation="150"
                >
                  <div
                    v-for="(row, index) in panel.payMethods"
                    :key="'control-' + row.id"
                    class="method-row"
                  >
                    <span class="drag-handle">≡</span>
                    <Select
                      v-model="row.payMethod"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                      @on-change="onMethodChange(panel, index, $event)"
                    >
                      <Option
                        v-for="item in getMethodOptions(controlPlatform)"
                        :key="'control-' + controlPlatform + '-method-' + item.dictValue"
                        :value="Number(item.dictValue)"
                        :disabled="
                          isMethodUsed(panel, Number(item.dictValue), index)
                        "
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Select
                      v-model="row.provider"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                    >
                      <Option
                        v-for="item in getFilteredProviders(panel, index)"
                        :key="'control-' + controlPlatform + '-provider-' + item.dictValue"
                        :value="Number(item.dictValue)"
                        :disabled="
                          (isWalletSelected(panel, index) &&
                            item.dictName !== 'PayPal') ||
                          (isAppleOrGooglePaySelected(panel, index) &&
                            item.dictName === 'PayPal')
                        "
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Radio
                      :value="row.isDefault"
                      :disabled="disabled"
                      @on-change="setDefaultPayMethod(panel, index)"
                    />
                    <a
                      v-if="
                        panel.payMethods.length > 1 &&
                        row.payMethod !== 0 &&
                        !disabled
                      "
                      class="row-delete"
                      @click="removePayMethod(panel, index)"
                    >
                      ×
                    </a>
                  </div>
                </draggable>
              </div>
            </div>

            <div v-if="panel.supportsGoods" class="goods-config-table">
              <draggable
                v-model="panel.goodsSections"
                handle=".drag-handle"
                :disabled="disabled || !panel.supportsGold"
                :animation="150"
              >
                <div
                  v-for="section in getPanelGoodsSections(panel)"
                  :key="'control-' + section.key"
                  class="goods-section"
                >
                  <template v-if="section.key === 'subscription'">
                    <div class="goods-group-title">
                      <span class="drag-handle group-handle">≡</span>
                      订阅付费项
                    </div>
                    <draggable
                      v-model="panel.subscriptionGoods"
                      handle=".row-handle"
                      :disabled="disabled"
                      :animation="150"
                    >
                      <div
                        v-for="(row, index) in panel.subscriptionGoods"
                        :key="'control-subscription-' + row.id"
                        class="subscription-item"
                      >
                        <div
                          class="subscription-main"
                          :class="{ 'without-third': !panel.thirdPay }"
                        >
                          <span class="drag-handle row-handle">≡</span>
                          <Poptip
                            v-model="row.visible"
                            class="goods-picker-poptip"
                            trigger="click"
                            :transfer="false"
                            placement="bottom-start"
                            popper-class="recharge-subscription-popper"
                            width="560"
                            padding="0"
                            :disabled="disabled"
                          >
                            <div class="goods-picker-trigger-wrap">
                              <div
                                class="goods-picker-trigger"
                                :class="{ disabled: disabled }"
                              >
                                <Tag
                                  v-if="row.goodsId"
                                  :color="row.goodsType === 1 ? 'blue' : 'orange'"
                                >
                                  {{ row.goodsType === 1 ? 'VIP' : '通行证' }}
                                </Tag>
                                <span class="trigger-text">
                                  {{ getGoodsTriggerText(row, controlPlatform) }}
                                </span>
                              </div>
                              <div
                                v-if="getSubscriptionTipGoods(row, controlPlatform)"
                                class="goods-mini-preview"
                              >
                                <div class="goods-mini-title">
                                  {{ getSubscriptionTipGoods(row, controlPlatform).goodsName }}
                                </div>
                                <div
                                  v-for="item in getSubscriptionTipRows(
                                    getSubscriptionTipGoods(row, controlPlatform),
                                    controlPlatform
                                  )"
                                  :key="'control-mini-' + item.key"
                                  class="goods-mini-line"
                                >
                                  <span>{{ item.label }}</span>
                                  <strong :class="item.className">
                                    {{ item.value }}
                                  </strong>
                                </div>
                              </div>
                            </div>
                            <div slot="content" class="subscription-picker">
                              <div class="picker-tabs">
                                <span
                                  :class="{ active: row.goodsType === 1 }"
                                  @click.stop="setSubscriptionType(row, 1)"
                                >
                                  VIP
                                </span>
                                <span
                                  :class="{ active: row.goodsType === 2 }"
                                  @click.stop="setSubscriptionType(row, 2)"
                                >
                                  通行证
                                </span>
                              </div>
                              <div class="picker-content">
                                <div class="picker-list">
                                  <div
                                    v-for="item in getSubscriptionOptions(
                                      controlPlatform,
                                      row
                                    )"
                                    :key="'control-sub-option-' + item.id"
                                    class="picker-item"
                                    :class="{
                                      selected: isSameGoodsId(row.goodsId, item.id),
                                      disabled: isSubscriptionGoodsUsed(
                                        panel.subscriptionGoods,
                                        row.goodsType,
                                        item.id,
                                        index
                                      )
                                    }"
                                    @mouseenter="setPreviewGoods(row, item.id)"
                                    @click.stop="
                                      !isSubscriptionGoodsUsed(
                                        panel.subscriptionGoods,
                                        row.goodsType,
                                        item.id,
                                        index
                                      ) && selectSubscriptionGoods(row, item)
                                    "
                                  >
                                    <Tag :color="row.goodsType === 1 ? 'blue' : 'orange'">
                                      {{ row.goodsType === 1 ? 'VIP' : '通行证' }}
                                    </Tag>
                                    <span class="picker-name">{{ item.goodsName }}</span>
                                    <span class="picker-price">
                                      {{ formatGoodsPrice(item) }}
                                    </span>
                                  </div>
                                  <div
                                    v-if="
                                      !getSubscriptionOptions(controlPlatform, row).length
                                    "
                                    class="empty-text"
                                  >
                                    暂无付费项
                                  </div>
                                </div>
                                <div class="picker-preview">
                                  <template v-if="getPreviewGoods(row, controlPlatform)">
                                    <div class="preview-title">
                                      {{ getPreviewGoods(row, controlPlatform).goodsName }}
                                    </div>
                                    <div
                                      v-for="item in getSubscriptionTipRows(
                                        getPreviewGoods(row, controlPlatform),
                                        controlPlatform
                                      )"
                                      :key="'control-prev-' + item.key"
                                      class="preview-line"
                                    >
                                      <span>{{ item.label }}</span>
                                      <strong :class="item.className">
                                        {{ item.value }}
                                      </strong>
                                    </div>
                                  </template>
                                  <div v-else class="empty-text">请选择付费项</div>
                                </div>
                              </div>
                            </div>
                          </Poptip>
                          <Checkbox
                            v-if="panel.thirdPay"
                            v-model="row.onlyThird"
                            :disabled="disabled"
                          >
                            仅三方
                          </Checkbox>
                          <a
                            v-if="panel.subscriptionGoods.length > 1 && !disabled"
                            class="row-delete"
                            @click="removeGoodsRow(panel.subscriptionGoods, index)"
                          >
                            ×
                          </a>
                        </div>
                        <div
                          v-if="
                            panel.thirdPay &&
                            row.onlyThird
                          "
                          class="subscription-default"
                        >
                          <div v-if="isStepThirdPay(panel)" class="step-pay-tip">
                            分步支付模式下，无需选择默认支付方式。
                          </div>
                          <div v-else class="default-line">
                            <label>默认支付方式：</label>
                            <Select
                              v-model="row.defaultPayMethod"
                              size="small"
                              
                              :disabled="disabled"
                              placeholder="支付方式"
                              @on-change="onDefaultPayMethodChange(row, $event)"
                            >
                              <Option
                                v-for="item in getThirdMethodOptions(controlPlatform)"
                                :key="'control-sub-method-' + item.dictValue"
                                :value="Number(item.dictValue)"
                              >
                                {{ item.dictName }}
                              </Option>
                            </Select>
                            <label class="inline-label">默认渠道：</label>
                            <Select
                              v-model="row.defaultProvider"
                              size="small"
                              
                              :disabled="disabled"
                              placeholder="渠道"
                            >
                              <Option
                                v-for="item in getDefaultProviderOptions(row.defaultPayMethod)"
                                :key="'control-sub-provider-' + item.dictValue"
                                :value="Number(item.dictValue)"
                              >
                                {{ item.dictName }}
                              </Option>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </draggable>
                    <div
                      v-if="!disabled"
                      class="add-row"
                      @click="addSubscriptionGoods(panel)"
                    >
                      + 新增订阅项
                    </div>
                  </template>

                  <template v-else-if="section.key === 'gold' && panel.supportsGold">
                    <div class="goods-group-title">
                      <span class="drag-handle group-handle">≡</span>
                      金币付费项
                    </div>
                    <draggable
                      v-model="panel.goldGoods"
                      handle=".row-handle"
                      :disabled="disabled"
                      :animation="150"
                      class="gold-grid"
                    >
                      <div
                        v-for="(row, index) in panel.goldGoods"
                        :key="'control-gold-' + row.id"
                        class="gold-item"
                      >
                        <Poptip
                          v-model="row.visible"
                          class="goods-picker-poptip"
                          trigger="click"
                          :transfer="false"
                          placement="bottom-start"
                          popper-class="recharge-gold-picker-popper"
                          width="560"
                          padding="0"
                          :disabled="disabled"
                        >
                          <div class="goods-picker-trigger-wrap">
                            <div
                              class="goods-picker-trigger"
                              :class="{ disabled: disabled }"
                            >
                              <Tag v-if="row.goodsId" color="orange">金币</Tag>
                              <span class="trigger-text">
                                {{ getGoldTriggerText(row, controlPlatform) }}
                              </span>
                            </div>
                            <div
                              v-if="getGoldPreview(row, controlPlatform)"
                              class="goods-mini-preview"
                            >
                              <div class="goods-mini-title">
                                {{ getGoldPreview(row, controlPlatform).goodsName }}
                              </div>
                              <div
                                v-for="item in getGoldTipRows(
                                  getGoldPreview(row, controlPlatform)
                                )"
                                :key="'control-gold-mini-' + item.key"
                                class="goods-mini-line"
                              >
                                <span>{{ item.label }}</span>
                                <strong :class="item.className">
                                  {{ item.value }}
                                </strong>
                              </div>
                            </div>
                          </div>
                          <div slot="content" class="subscription-picker gold-picker">
                            <div class="picker-content">
                              <div class="picker-list">
                                <div
                                  v-for="item in getGoodsOptions(controlPlatform, 'gold')"
                                  :key="'control-gold-option-' + item.id"
                                  class="picker-item"
                                  :class="{
                                    selected: isSameGoodsId(row.goodsId, item.id),
                                    disabled: isGoodsUsed(
                                      panel.goldGoods,
                                      item.id,
                                      index
                                    )
                                  }"
                                  @mouseenter="setGoldPreviewGoods(row, item.id)"
                                  @click.stop="
                                    !isGoodsUsed(panel.goldGoods, item.id, index) &&
                                      selectGoldGoods(row, item)
                                  "
                                >
                                  <Tag color="orange">金币</Tag>
                                  <span class="picker-name">{{ item.goodsName }}</span>
                                  <span class="picker-price">
                                    {{ formatGoodsPrice(item) }}
                                  </span>
                                </div>
                                <div
                                  v-if="!getGoodsOptions(controlPlatform, 'gold').length"
                                  class="empty-text"
                                >
                                  暂无付费项
                                </div>
                              </div>
                              <div class="picker-preview">
                                <template v-if="getGoldPickerPreview(row, controlPlatform)">
                                  <div class="preview-title">
                                    {{ getGoldPickerPreview(row, controlPlatform).goodsName }}
                                  </div>
                                  <div
                                    v-for="item in getGoldTipRows(
                                      getGoldPickerPreview(row, controlPlatform)
                                    )"
                                    :key="'control-gold-prev-' + item.key"
                                    class="preview-line"
                                  >
                                    <span>{{ item.label }}</span>
                                    <strong :class="item.className">
                                      {{ item.value }}
                                    </strong>
                                  </div>
                                </template>
                                <div v-else class="empty-text">请选择付费项</div>
                              </div>
                            </div>
                          </div>
                        </Poptip>
                        <a
                          v-if="panel.goldGoods.length > 1 && !disabled"
                          class="row-delete"
                          @click="removeGoldGoodsRow(panel, index)"
                        >
                          ×
                        </a>
                      </div>
                    </draggable>
                    <div
                      v-if="!disabled"
                      class="add-row"
                      @click="addGoldGoods(panel)"
                    >
                      + 新增金币项
                    </div>
                  </template>
                </div>
              </draggable>
            </div>

            <div v-if="panel.key === 'paywall'" class="ad-tag-box">
              <div class="ad-tag-title">
                广告解锁层
                <span class="inner-purchase-tag">仅内购</span>
              </div>
              <div class="ad-tag-picker-row">
                <Poptip
                  v-model="panel.adTagVisible"
                  class="goods-picker-poptip"
                  trigger="click"
                  
                  placement="bottom-start"
                  popper-class="recharge-subscription-popper"
                  width="560"
                  padding="0"
                  :disabled="disabled"
                >
                  <div class="goods-picker-trigger-wrap">
                    <div
                      class="goods-picker-trigger"
                      :class="{ disabled: disabled }"
                    >
                      <Tag v-if="panel.adTagGoodsId" color="blue">VIP</Tag>
                      <span class="trigger-text">
                        {{ getAdTagTriggerText(panel, controlPlatform) }}
                      </span>
                    </div>
                    <div
                      v-if="getAdTagSelectedGoods(panel, controlPlatform)"
                      class="goods-mini-preview"
                    >
                      <div class="goods-mini-title">
                        {{ getAdTagSelectedGoods(panel, controlPlatform).goodsName }}
                      </div>
                      <div
                        v-for="item in getSubscriptionTipRows(
                          getAdTagSelectedGoods(panel, controlPlatform),
                          controlPlatform
                        )"
                        :key="'control-ad-mini-' + item.key"
                        class="goods-mini-line"
                      >
                        <span>{{ item.label }}</span>
                        <strong :class="item.className">
                          {{ item.value }}
                        </strong>
                      </div>
                    </div>
                  </div>
                  <div slot="content" class="subscription-picker">
                    <div class="picker-content">
                      <div class="picker-list">
                        <div
                          v-for="item in getAdTagGoodsOptions(controlPlatform)"
                          :key="'control-ad-option-' + item.id"
                          class="picker-item"
                          :class="{
                            selected: isSameGoodsId(panel.adTagGoodsId, item.id)
                          }"
                          @mouseenter="setAdTagPreviewGoods(panel, item.id)"
                          @click.stop="selectAdTagGoods(panel, item)"
                        >
                          <Tag color="blue">VIP</Tag>
                          <span class="picker-name">{{ item.goodsName }}</span>
                          <span class="picker-price">
                            {{ formatGoodsPrice(item) }}
                          </span>
                        </div>
                        <div
                          v-if="!getAdTagGoodsOptions(controlPlatform).length"
                          class="empty-text"
                        >
                          暂无付费项
                        </div>
                      </div>
                      <div class="picker-preview">
                        <template v-if="getAdTagPickerPreview(panel, controlPlatform)">
                          <div class="preview-title">
                            {{ getAdTagPickerPreview(panel, controlPlatform).goodsName }}
                          </div>
                          <div
                            v-for="item in getSubscriptionTipRows(
                              getAdTagPickerPreview(panel, controlPlatform),
                              controlPlatform
                            )"
                            :key="'control-ad-prev-' + item.key"
                            class="preview-line"
                          >
                            <span>{{ item.label }}</span>
                            <strong :class="item.className">
                              {{ item.value }}
                            </strong>
                          </div>
                        </template>
                        <div v-else class="empty-text">请选择付费项</div>
                      </div>
                    </div>
                  </div>
                </Poptip>
                <a
                  v-if="panel.adTagGoodsId && !disabled"
                  class="row-delete"
                  @click="clearAdTagGoods(panel)"
                >
                  ×
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="wide-section">
          <div class="section-title">活动三方配置</div>
          <div class="wide-card">
            <div class="switch-box">
              <i-switch v-model="controlCurrentConfig.activity.thirdPay" :disabled="disabled" />
              <span>三方支付</span>
            </div>
            <div v-show="controlCurrentConfig.activity.thirdPay" class="activity-content">
              <div class="activity-row">
                <span>活动H5</span>
                <Select
                  v-model="controlCurrentConfig.activity.pushRuleIds"
                  multiple
                  :disabled="disabled"
                  filterable
                  :transfer="false"
                  popper-class="recharge-activity-popper"
                >
                  <Option
                    v-for="item in activityOptions"
                    :key="'control-activity-push-rule-' + item.id"
                    :value="item.id"
                    :label="item.label"
                  >
                    {{ item.label }}
                  </Option>
                </Select>
              </div>
              <div class="table-title">
                <span>支付方式</span>
                <Button
                  size="small"
                  :disabled="disabled || controlCurrentConfig.activity.payMethods.length >= maxMethodCount"
                  @click="addPayMethod(controlCurrentConfig.activity)"
                >
                  + 新增
                </Button>
              </div>
              <div class="method-table simple-table">
                <div class="method-head">
                  <span>序</span>
                  <span>支付方式</span>
                  <span>渠道</span>
                  <span>默认</span>
                  <span></span>
                </div>
                <draggable
                  v-model="controlCurrentConfig.activity.payMethods"
                  handle=".drag-handle"
                  :disabled="disabled"
                  :animation="150"
                >
                  <div
                    v-for="(row, index) in controlCurrentConfig.activity.payMethods"
                    :key="'control-act-' + row.id"
                    class="method-row"
                  >
                    <span class="drag-handle">≡</span>
                    <Select
                      v-model="row.payMethod"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                      @on-change="onMethodChange(controlCurrentConfig.activity, index, $event)"
                    >
                      <Option
                        v-for="item in getMethodOptions(controlPlatform)"
                        :key="'control-' + controlPlatform + '-act-method-' + item.dictValue"
                        :value="Number(item.dictValue)"
                        :disabled="
                          isMethodUsed(controlCurrentConfig.activity, Number(item.dictValue), index)
                        "
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Select
                      v-model="row.provider"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                    >
                      <Option
                        v-for="item in getFilteredProviders(controlCurrentConfig.activity, index)"
                        :key="'control-' + controlPlatform + '-act-provider-' + item.dictValue"
                        :value="Number(item.dictValue)"
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Radio
                      :value="row.isDefault"
                      :disabled="disabled"
                      @on-change="setDefaultPayMethod(controlCurrentConfig.activity, index)"
                    />
                    <a
                      v-if="
                        controlCurrentConfig.activity.payMethods.length > 1 &&
                        row.payMethod !== 0 &&
                        !disabled
                      "
                      class="row-delete"
                      @click="removePayMethod(controlCurrentConfig.activity, index)"
                    >
                      ×
                    </a>
                  </div>
                </draggable>
              </div>
            </div>
          </div>
        </div>

        <div class="wide-section">
          <div class="section-title">折扣弹窗</div>
          <div class="wide-card">
            <div class="switch-box">
              <i-switch v-model="controlCurrentConfig.discount.thirdPay" :disabled="disabled" />
              <span>三方支付</span>
            </div>
            <div v-show="controlCurrentConfig.discount.thirdPay" class="activity-content">
              <div class="table-title">
                <span>支付方式</span>
                <Button
                  size="small"
                  :disabled="disabled || controlCurrentConfig.discount.payMethods.length >= maxMethodCount"
                  @click="addPayMethod(controlCurrentConfig.discount)"
                >
                  + 新增
                </Button>
              </div>
              <div class="method-table simple-table">
                <div class="method-head">
                  <span>序</span>
                  <span>支付方式</span>
                  <span>渠道</span>
                  <span>默认</span>
                  <span></span>
                </div>
                <draggable
                  v-model="controlCurrentConfig.discount.payMethods"
                  handle=".drag-handle"
                  :disabled="disabled"
                  :animation="150"
                >
                  <div
                    v-for="(row, index) in controlCurrentConfig.discount.payMethods"
                    :key="'control-dis-' + row.id"
                    class="method-row"
                  >
                    <span class="drag-handle">≡</span>
                    <Select
                      v-model="row.payMethod"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                      @on-change="onMethodChange(controlCurrentConfig.discount, index, $event)"
                    >
                      <Option
                        v-for="item in getMethodOptions(controlPlatform)"
                        :key="'control-' + controlPlatform + '-dis-method-' + item.dictValue"
                        :value="Number(item.dictValue)"
                        :disabled="
                          isMethodUsed(controlCurrentConfig.discount, Number(item.dictValue), index)
                        "
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Select
                      v-model="row.provider"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                    >
                      <Option
                        v-for="item in getFilteredProviders(controlCurrentConfig.discount, index)"
                        :key="'control-' + controlPlatform + '-dis-provider-' + item.dictValue"
                        :value="Number(item.dictValue)"
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Radio
                      :value="row.isDefault"
                      :disabled="disabled"
                      @on-change="setDefaultPayMethod(controlCurrentConfig.discount, index)"
                    />
                    <a
                      v-if="
                        controlCurrentConfig.discount.payMethods.length > 1 &&
                        row.payMethod !== 0 &&
                        !disabled
                      "
                      class="row-delete"
                      @click="removePayMethod(controlCurrentConfig.discount, index)"
                    >
                      ×
                    </a>
                  </div>
                </draggable>
              </div>
            </div>
          </div>
        </div>
      </div>
      

      <div
        v-for="(group, groupIndex) in formData.groups || []"
        :key="'experiment-group-' + groupIndex"
        class="group-card"
      >
        <h3 class="group-title">{{ getExperimentGroupTitle(groupIndex) }}</h3>
        
        <div class="form-line">
          <FormItem
            label="选择模板"
            prop="templateId"
          >
            <Select
              v-model="group.experimentTemplateId"
              class="template-select"
              placeholder="— 选择策略模板 —"
              filterable
              remote
              clearable
              
              :remote-method="searchTemplateOptions"
              :loading="templateSearchLoading || templateDetailLoading"
              :disabled="disabled"
              @on-change="() => handleExperimentTemplateChange(groupIndex)"
            >
              <Option
                v-for="item in templateOptions"
                :key="item.value"
                :value="item.value"
                :label="item.label"
              >
                {{ item.label }}
              </Option>
            </Select>
          </FormItem>
          <span class="form-tip">选择后自动填充下方配置</span>
        </div>

        <Tabs :value="getExperimentPlatform(groupIndex)" @on-click="(name) => setExperimentPlatform(groupIndex, name)" :animated="false" class="group-tabs">
          <TabPane label="IOS 配置" name="ios" />
          <TabPane label="Android 配置" name="android" />
        </Tabs>

        <div class="section-title">充值面板配置</div>
        <div class="payment-grid">
          <div
            v-for="panel in getExperimentGroupConfig(groupIndex).panels"
            :key="'experiment-' + groupIndex + '-' + getExperimentPlatform(groupIndex) + '-' + panel.key"
            class="payment-card"
          >
            <div class="payment-title">{{ panel.title }}</div>
            <div class="switch-box">
              <i-switch v-model="panel.thirdPay" :disabled="disabled" />
              <span>三方支付</span>
            </div>

            <div v-show="panel.thirdPay">
              <RadioGroup v-model="panel.payLinkType" class="radio-row">
                <Radio :label="0" :disabled="disabled">聚合</Radio>
                <Radio :label="1" :disabled="disabled">分步</Radio>
              </RadioGroup>

              <div class="table-title">
                <span>支付方式</span>
                <Button
                  size="small"
                  :disabled="disabled || panel.payMethods.length >= maxMethodCount"
                  @click="addPayMethod(panel)"
                >
                  + 新增
                </Button>
              </div>
              <div class="method-table">
                <div class="method-head">
                  <span>序</span>
                  <span>支付方式</span>
                  <span>渠道</span>
                  <span>默认</span>
                  <span></span>
                </div>
                <draggable
                  v-model="panel.payMethods"
                  handle=".drag-handle"
                  :disabled="disabled"
                  :animation="150"
                >
                  <div
                    v-for="(row, index) in panel.payMethods"
                    :key="row.id"
                    class="method-row"
                  >
                    <span class="drag-handle">≡</span>
                    <Select
                      v-model="row.payMethod"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                      @on-change="onMethodChange(panel, index, $event)"
                    >
                      <Option
                        v-for="item in getMethodOptions(getExperimentPlatform(groupIndex))"
                        :key="getExperimentPlatform(groupIndex) + '-method-' + item.dictValue"
                        :value="Number(item.dictValue)"
                        :disabled="
                          isMethodUsed(panel, Number(item.dictValue), index)
                        "
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Select
                      v-model="row.provider"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                    >
                      <Option
                        v-for="item in getFilteredProviders(panel, index)"
                        :key="getExperimentPlatform(groupIndex) + '-provider-' + item.dictValue"
                        :value="Number(item.dictValue)"
                        :disabled="
                          (isWalletSelected(panel, index) &&
                            item.dictName !== 'PayPal') ||
                          (isAppleOrGooglePaySelected(panel, index) &&
                            item.dictName === 'PayPal')
                        "
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Radio
                      :value="row.isDefault"
                      :disabled="disabled"
                      @on-change="setDefaultPayMethod(panel, index)"
                    />
                    <a
                      v-if="
                        panel.payMethods.length > 1 &&
                        row.payMethod !== 0 &&
                        !disabled
                      "
                      class="row-delete"
                      @click="removePayMethod(panel, index)"
                    >
                      ×
                    </a>
                  </div>
                </draggable>
              </div>
            </div>

            <div v-if="panel.supportsGoods" class="goods-config-table">
              <draggable
                v-model="panel.goodsSections"
                handle=".drag-handle"
                :disabled="disabled || !panel.supportsGold"
                :animation="150"
              >
                <div
                  v-for="section in getPanelGoodsSections(panel)"
                  :key="section.key"
                  class="goods-section"
                >
                  <template v-if="section.key === 'subscription'">
                    <div class="goods-group-title">
                      <span class="drag-handle group-handle">≡</span>
                      订阅付费项
                    </div>
                    <draggable
                      v-model="panel.subscriptionGoods"
                      handle=".row-handle"
                      :disabled="disabled"
                      :animation="150"
                    >
                      <div
                        v-for="(row, index) in panel.subscriptionGoods"
                        :key="'subscription-' + row.id"
                        class="subscription-item"
                      >
                        <div
                          class="subscription-main"
                          :class="{ 'without-third': !panel.thirdPay }"
                        >
                          <span class="drag-handle row-handle">≡</span>
                          <Poptip
                            v-model="row.visible"
                            class="goods-picker-poptip"
                            trigger="click"
                            
                            placement="bottom-start"
                            popper-class="recharge-subscription-popper"
                            width="560"
                            padding="0"
                            :disabled="disabled"
                          >
                            <div class="goods-picker-trigger-wrap">
                              <div
                                class="goods-picker-trigger"
                                :class="{ disabled: disabled }"
                              >
                                <Tag
                                  v-if="row.goodsId"
                                  :color="row.goodsType === 1 ? 'blue' : 'orange'"
                                >
                                  {{ row.goodsType === 1 ? 'VIP' : '通行证' }}
                                </Tag>
                                <span class="trigger-text">
                                  {{ getGoodsTriggerText(row, getExperimentPlatform(groupIndex)) }}
                                </span>
                              </div>
                              <div
                                v-if="getSubscriptionTipGoods(row, getExperimentPlatform(groupIndex))"
                                class="goods-mini-preview"
                              >
                                <div class="goods-mini-title">
                                  {{ getSubscriptionTipGoods(row, getExperimentPlatform(groupIndex)).goodsName }}
                                </div>
                                <div
                                  v-for="item in getSubscriptionTipRows(
                                    getSubscriptionTipGoods(row, getExperimentPlatform(groupIndex)),
                                    getExperimentPlatform(groupIndex)
                                  )"
                                  :key="item.key"
                                  class="goods-mini-line"
                                >
                                  <span>{{ item.label }}</span>
                                  <strong :class="item.className">
                                    {{ item.value }}
                                  </strong>
                                </div>
                              </div>
                            </div>
                            <div slot="content" class="subscription-picker">
                              <div class="picker-tabs">
                                <span
                                  :class="{ active: row.goodsType === 1 }"
                                  @click.stop="setSubscriptionType(row, 1)"
                                >
                                  VIP
                                </span>
                                <span
                                  :class="{ active: row.goodsType === 2 }"
                                  @click.stop="setSubscriptionType(row, 2)"
                                >
                                  通行证
                                </span>
                              </div>
                              <div class="picker-content">
                                <div class="picker-list">
                                  <div
                                    v-for="item in getSubscriptionOptions(
                                      getExperimentPlatform(groupIndex),
                                      row
                                    )"
                                    :key="'subscription-option-' + item.id"
                                    class="picker-item"
                                    :class="{
                                      selected: isSameGoodsId(row.goodsId, item.id),
                                      disabled: isSubscriptionGoodsUsed(
                                        panel.subscriptionGoods,
                                        row.goodsType,
                                        item.id,
                                        index
                                      )
                                    }"
                                    @mouseenter="setPreviewGoods(row, item.id)"
                                    @click.stop="
                                      !isSubscriptionGoodsUsed(
                                        panel.subscriptionGoods,
                                        row.goodsType,
                                        item.id,
                                        index
                                      ) && selectSubscriptionGoods(row, item)
                                    "
                                  >
                                    <Tag :color="row.goodsType === 1 ? 'blue' : 'orange'">
                                      {{ row.goodsType === 1 ? 'VIP' : '通行证' }}
                                    </Tag>
                                    <span class="picker-name">{{ item.goodsName }}</span>
                                    <span class="picker-price">
                                      {{ formatGoodsPrice(item) }}
                                    </span>
                                  </div>
                                  <div
                                    v-if="
                                      !getSubscriptionOptions(getExperimentPlatform(groupIndex), row).length
                                    "
                                    class="empty-text"
                                  >
                                    暂无付费项
                                  </div>
                                </div>
                                <div class="picker-preview">
                                  <template v-if="getPreviewGoods(row, getExperimentPlatform(groupIndex))">
                                    <div class="preview-title">
                                      {{ getPreviewGoods(row, getExperimentPlatform(groupIndex)).goodsName }}
                                    </div>
                                    <div
                                      v-for="item in getSubscriptionTipRows(
                                        getPreviewGoods(row, getExperimentPlatform(groupIndex)),
                                        getExperimentPlatform(groupIndex)
                                      )"
                                      :key="'prev-' + item.key"
                                      class="preview-line"
                                    >
                                      <span>{{ item.label }}</span>
                                      <strong :class="item.className">
                                        {{ item.value }}
                                      </strong>
                                    </div>
                                  </template>
                                  <div v-else class="empty-text">请选择付费项</div>
                                </div>
                              </div>
                            </div>
                          </Poptip>
                          <Checkbox
                            v-if="panel.thirdPay"
                            v-model="row.onlyThird"
                            :disabled="disabled"
                          >
                            仅三方
                          </Checkbox>
                          <a
                            v-if="panel.subscriptionGoods.length > 1 && !disabled"
                            class="row-delete"
                            @click="removeGoodsRow(panel.subscriptionGoods, index)"
                          >
                            ×
                          </a>
                        </div>
                        <div
                          v-if="
                            panel.thirdPay &&
                            row.onlyThird
                          "
                          class="subscription-default"
                        >
                          <div v-if="isStepThirdPay(panel)" class="step-pay-tip">
                            分步支付模式下，无需选择默认支付方式。
                          </div>
                          <div v-else class="default-line">
                            <label>默认支付方式：</label>
                            <Select
                              v-model="row.defaultPayMethod"
                              size="small"
                              
                              :disabled="disabled"
                              placeholder="支付方式"
                              @on-change="onDefaultPayMethodChange(row, $event)"
                            >
                              <Option
                                v-for="item in getThirdMethodOptions(getExperimentPlatform(groupIndex))"
                                :key="'sub-method-' + item.dictValue"
                                :value="Number(item.dictValue)"
                              >
                                {{ item.dictName }}
                              </Option>
                            </Select>
                            <label class="inline-label">默认渠道：</label>
                            <Select
                              v-model="row.defaultProvider"
                              size="small"
                              
                              :disabled="disabled"
                              placeholder="渠道"
                            >
                              <Option
                                v-for="item in getDefaultProviderOptions(row.defaultPayMethod)"
                                :key="'sub-provider-' + item.dictValue"
                                :value="Number(item.dictValue)"
                              >
                                {{ item.dictName }}
                              </Option>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </draggable>
                    <div
                      v-if="!disabled"
                      class="add-row"
                      @click="addSubscriptionGoods(panel)"
                    >
                      + 新增订阅项
                    </div>
                  </template>

                  <template v-else-if="section.key === 'gold' && panel.supportsGold">
                    <div class="goods-group-title">
                      <span class="drag-handle group-handle">≡</span>
                      金币付费项
                    </div>
                    <draggable
                      v-model="panel.goldGoods"
                      handle=".row-handle"
                      :disabled="disabled"
                      :animation="150"
                      class="gold-grid"
                    >
                      <div
                        v-for="(row, index) in panel.goldGoods"
                        :key="'gold-' + row.id"
                        class="gold-item"
                      >
                        <Poptip
                          v-model="row.visible"
                          class="goods-picker-poptip"
                          trigger="click"
                          
                          placement="bottom-start"
                          popper-class="recharge-gold-picker-popper"
                          width="560"
                          padding="0"
                          :disabled="disabled"
                        >
                          <div class="goods-picker-trigger-wrap">
                            <div
                              class="goods-picker-trigger"
                              :class="{ disabled: disabled }"
                            >
                              <Tag v-if="row.goodsId" color="orange">金币</Tag>
                              <span class="trigger-text">
                                {{ getGoldTriggerText(row, getExperimentPlatform(groupIndex)) }}
                              </span>
                            </div>
                            <div
                              v-if="getGoldPreview(row, getExperimentPlatform(groupIndex))"
                              class="goods-mini-preview"
                            >
                              <div class="goods-mini-title">
                                {{ getGoldPreview(row, getExperimentPlatform(groupIndex)).goodsName }}
                              </div>
                              <div
                                v-for="item in getGoldTipRows(
                                  getGoldPreview(row, getExperimentPlatform(groupIndex))
                                )"
                                :key="'gold-mini-' + item.key"
                                class="goods-mini-line"
                              >
                                <span>{{ item.label }}</span>
                                <strong :class="item.className">
                                  {{ item.value }}
                                </strong>
                              </div>
                            </div>
                          </div>
                          <div slot="content" class="subscription-picker gold-picker">
                            <div class="picker-content">
                              <div class="picker-list">
                                <div
                                  v-for="item in getGoodsOptions(getExperimentPlatform(groupIndex), 'gold')"
                                  :key="'gold-option-' + item.id"
                                  class="picker-item"
                                  :class="{
                                    selected: isSameGoodsId(row.goodsId, item.id),
                                    disabled: isGoodsUsed(
                                      panel.goldGoods,
                                      item.id,
                                      index
                                    )
                                  }"
                                  @mouseenter="setGoldPreviewGoods(row, item.id)"
                                  @click.stop="
                                    !isGoodsUsed(panel.goldGoods, item.id, index) &&
                                      selectGoldGoods(row, item)
                                  "
                                >
                                  <Tag color="orange">金币</Tag>
                                  <span class="picker-name">{{ item.goodsName }}</span>
                                  <span class="picker-price">
                                    {{ formatGoodsPrice(item) }}
                                  </span>
                                </div>
                                <div
                                  v-if="!getGoodsOptions(getExperimentPlatform(groupIndex), 'gold').length"
                                  class="empty-text"
                                >
                                  暂无付费项
                                </div>
                              </div>
                              <div class="picker-preview">
                                <template v-if="getGoldPickerPreview(row, getExperimentPlatform(groupIndex))">
                                  <div class="preview-title">
                                    {{ getGoldPickerPreview(row, getExperimentPlatform(groupIndex)).goodsName }}
                                  </div>
                                  <div
                                    v-for="item in getGoldTipRows(
                                      getGoldPickerPreview(row, getExperimentPlatform(groupIndex))
                                    )"
                                    :key="'gold-prev-' + item.key"
                                    class="preview-line"
                                  >
                                    <span>{{ item.label }}</span>
                                    <strong :class="item.className">
                                      {{ item.value }}
                                    </strong>
                                  </div>
                                </template>
                                <div v-else class="empty-text">请选择付费项</div>
                              </div>
                            </div>
                          </div>
                        </Poptip>
                        <a
                          v-if="panel.goldGoods.length > 1 && !disabled"
                          class="row-delete"
                          @click="removeGoldGoodsRow(panel, index)"
                        >
                          ×
                        </a>
                      </div>
                    </draggable>
                    <div
                      v-if="!disabled"
                      class="add-row"
                      @click="addGoldGoods(panel)"
                    >
                      + 新增金币项
                    </div>
                  </template>
                </div>
              </draggable>
            </div>

            <div v-if="panel.key === 'paywall'" class="ad-tag-box">
              <div class="ad-tag-title">
                广告解锁层
                <span class="inner-purchase-tag">仅内购</span>
              </div>
              <div class="ad-tag-picker-row">
                <Poptip
                  v-model="panel.adTagVisible"
                  class="goods-picker-poptip"
                  trigger="click"
                  
                  placement="bottom-start"
                  popper-class="recharge-subscription-popper"
                  width="560"
                  padding="0"
                  :disabled="disabled"
                >
                  <div class="goods-picker-trigger-wrap">
                    <div
                      class="goods-picker-trigger"
                      :class="{ disabled: disabled }"
                    >
                      <Tag v-if="panel.adTagGoodsId" color="blue">VIP</Tag>
                      <span class="trigger-text">
                        {{ getAdTagTriggerText(panel, getExperimentPlatform(groupIndex)) }}
                      </span>
                    </div>
                    <div
                      v-if="getAdTagSelectedGoods(panel, getExperimentPlatform(groupIndex))"
                      class="goods-mini-preview"
                    >
                      <div class="goods-mini-title">
                        {{ getAdTagSelectedGoods(panel, getExperimentPlatform(groupIndex)).goodsName }}
                      </div>
                      <div
                        v-for="item in getSubscriptionTipRows(
                          getAdTagSelectedGoods(panel, getExperimentPlatform(groupIndex)),
                          getExperimentPlatform(groupIndex)
                        )"
                        :key="'ad-mini-' + item.key"
                        class="goods-mini-line"
                      >
                        <span>{{ item.label }}</span>
                        <strong :class="item.className">
                          {{ item.value }}
                        </strong>
                      </div>
                    </div>
                  </div>
                  <div slot="content" class="subscription-picker">
                    <div class="picker-content">
                      <div class="picker-list">
                        <div
                          v-for="item in getAdTagGoodsOptions(getExperimentPlatform(groupIndex))"
                          :key="'ad-option-' + item.id"
                          class="picker-item"
                          :class="{
                            selected: isSameGoodsId(panel.adTagGoodsId, item.id)
                          }"
                          @mouseenter="setAdTagPreviewGoods(panel, item.id)"
                          @click.stop="selectAdTagGoods(panel, item)"
                        >
                          <Tag color="blue">VIP</Tag>
                          <span class="picker-name">{{ item.goodsName }}</span>
                          <span class="picker-price">
                            {{ formatGoodsPrice(item) }}
                          </span>
                        </div>
                        <div
                          v-if="!getAdTagGoodsOptions(getExperimentPlatform(groupIndex)).length"
                          class="empty-text"
                        >
                          暂无付费项
                        </div>
                      </div>
                      <div class="picker-preview">
                        <template v-if="getAdTagPickerPreview(panel, getExperimentPlatform(groupIndex))">
                          <div class="preview-title">
                            {{ getAdTagPickerPreview(panel, getExperimentPlatform(groupIndex)).goodsName }}
                          </div>
                          <div
                            v-for="item in getSubscriptionTipRows(
                              getAdTagPickerPreview(panel, getExperimentPlatform(groupIndex)),
                              getExperimentPlatform(groupIndex)
                            )"
                            :key="'ad-prev-' + item.key"
                            class="preview-line"
                          >
                            <span>{{ item.label }}</span>
                            <strong :class="item.className">
                              {{ item.value }}
                            </strong>
                          </div>
                        </template>
                        <div v-else class="empty-text">请选择付费项</div>
                      </div>
                    </div>
                  </div>
                </Poptip>
                <a
                  v-if="panel.adTagGoodsId && !disabled"
                  class="row-delete"
                  @click="clearAdTagGoods(panel)"
                >
                  ×
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="wide-section">
          <div class="section-title">活动三方配置</div>
          <div class="wide-card">
            <div class="switch-box">
              <i-switch v-model="getExperimentGroupConfig(groupIndex).activity.thirdPay" :disabled="disabled" />
              <span>三方支付</span>
            </div>
            <div v-show="getExperimentGroupConfig(groupIndex).activity.thirdPay" class="activity-content">
              <div class="activity-row">
                <span>活动H5</span>
                <Select
                  v-model="getExperimentGroupConfig(groupIndex).activity.pushRuleIds"
                  multiple
                  
                  :disabled="disabled"
                  filterable
                  :transfer="false"
                  popper-class="recharge-activity-popper"
                >
                  <Option
                    v-for="item in activityOptions"
                    :key="'activity-push-rule-' + item.id"
                    :value="item.id"
                    :label="item.label"
                  >
                    {{ item.label }}
                  </Option>
                </Select>
              </div>
              <div class="table-title">
                <span>支付方式</span>
                <Button
                  size="small"
                  :disabled="disabled || getExperimentGroupConfig(groupIndex).activity.payMethods.length >= maxMethodCount"
                  @click="addPayMethod(getExperimentGroupConfig(groupIndex).activity)"
                >
                  + 新增
                </Button>
              </div>
              <div class="method-table simple-table">
                <div class="method-head">
                  <span>序</span>
                  <span>支付方式</span>
                  <span>渠道</span>
                  <span>默认</span>
                  <span></span>
                </div>
                <draggable
                  v-model="getExperimentGroupConfig(groupIndex).activity.payMethods"
                  handle=".drag-handle"
                  :disabled="disabled"
                  :animation="150"
                >
                  <div
                    v-for="(row, index) in getExperimentGroupConfig(groupIndex).activity.payMethods"
                    :key="'act-' + row.id"
                    class="method-row"
                  >
                    <span class="drag-handle">≡</span>
                    <Select
                      v-model="row.payMethod"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                      @on-change="onMethodChange(getExperimentGroupConfig(groupIndex).activity, index, $event)"
                    >
                      <Option
                        v-for="item in getMethodOptions(getExperimentPlatform(groupIndex))"
                        :key="getExperimentPlatform(groupIndex) + '-act-method-' + item.dictValue"
                        :value="Number(item.dictValue)"
                        :disabled="
                          isMethodUsed(getExperimentGroupConfig(groupIndex).activity, Number(item.dictValue), index)
                        "
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Select
                      v-model="row.provider"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                    >
                      <Option
                        v-for="item in getFilteredProviders(getExperimentGroupConfig(groupIndex).activity, index)"
                        :key="getExperimentPlatform(groupIndex) + '-act-provider-' + item.dictValue"
                        :value="Number(item.dictValue)"
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Radio
                      :value="row.isDefault"
                      :disabled="disabled"
                      @on-change="setDefaultPayMethod(getExperimentGroupConfig(groupIndex).activity, index)"
                    />
                    <a
                      v-if="
                        getExperimentGroupConfig(groupIndex).activity.payMethods.length > 1 &&
                        row.payMethod !== 0 &&
                        !disabled
                      "
                      class="row-delete"
                      @click="removePayMethod(getExperimentGroupConfig(groupIndex).activity, index)"
                    >
                      ×
                    </a>
                  </div>
                </draggable>
              </div>
            </div>
          </div>
        </div>

        <div class="wide-section">
          <div class="section-title">折扣弹窗</div>
          <div class="wide-card">
            <div class="switch-box">
              <i-switch v-model="getExperimentGroupConfig(groupIndex).discount.thirdPay" :disabled="disabled" />
              <span>三方支付</span>
            </div>
            <div v-show="getExperimentGroupConfig(groupIndex).discount.thirdPay" class="activity-content">
              <div class="table-title">
                <span>支付方式</span>
                <Button
                  size="small"
                  :disabled="disabled || getExperimentGroupConfig(groupIndex).discount.payMethods.length >= maxMethodCount"
                  @click="addPayMethod(getExperimentGroupConfig(groupIndex).discount)"
                >
                  + 新增
                </Button>
              </div>
              <div class="method-table simple-table">
                <div class="method-head">
                  <span>序</span>
                  <span>支付方式</span>
                  <span>渠道</span>
                  <span>默认</span>
                  <span></span>
                </div>
                <draggable
                  v-model="getExperimentGroupConfig(groupIndex).discount.payMethods"
                  handle=".drag-handle"
                  :disabled="disabled"
                  :animation="150"
                >
                  <div
                    v-for="(row, index) in getExperimentGroupConfig(groupIndex).discount.payMethods"
                    :key="'dis-' + row.id"
                    class="method-row"
                  >
                    <span class="drag-handle">≡</span>
                    <Select
                      v-model="row.payMethod"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                      @on-change="onMethodChange(getExperimentGroupConfig(groupIndex).discount, index, $event)"
                    >
                      <Option
                        v-for="item in getMethodOptions(getExperimentPlatform(groupIndex))"
                        :key="getExperimentPlatform(groupIndex) + '-dis-method-' + item.dictValue"
                        :value="Number(item.dictValue)"
                        :disabled="
                          isMethodUsed(getExperimentGroupConfig(groupIndex).discount, Number(item.dictValue), index)
                        "
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Select
                      v-model="row.provider"
                      size="small"
                      
                      :disabled="disabled || row.payMethod === 0"
                    >
                      <Option
                        v-for="item in getFilteredProviders(getExperimentGroupConfig(groupIndex).discount, index)"
                        :key="getExperimentPlatform(groupIndex) + '-dis-provider-' + item.dictValue"
                        :value="Number(item.dictValue)"
                      >
                        {{ item.dictName }}
                      </Option>
                    </Select>
                    <Radio
                      :value="row.isDefault"
                      :disabled="disabled"
                      @on-change="setDefaultPayMethod(getExperimentGroupConfig(groupIndex).discount, index)"
                    />
                    <a
                      v-if="
                        getExperimentGroupConfig(groupIndex).discount.payMethods.length > 1 &&
                        row.payMethod !== 0 &&
                        !disabled
                      "
                      class="row-delete"
                      @click="removePayMethod(getExperimentGroupConfig(groupIndex).discount, index)"
                    >
                      ×
                    </a>
                  </div>
                </draggable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getGoodsClassification } from '@/api/goods'
import { getActivityPushRuleList } from '@/api/activityPush'
import { getRechargeTemplateConfigInfo, getRechargeTemplateConfigPage } from '@/api/fee'
import { debounce } from 'lodash'
import draggable from 'vuedraggable'
import { normalizeGroupParamByCapability } from '../helpers'

const PLATFORM_CHANNEL_MAP = {
  ios: 0,
  android: 1
}

const PANEL_ENTRY_MAP = {
  paywall: 0,
  wallet: 1,
  vip: 2
}

const DISCOUNT_ENTRY_ID = 3
const ACTIVITY_ENTRY_ID = 4

const VIP_DURATION_MAP = [
  '一周',
  '四周',
  '一个月',
  '两个月',
  '三个月',
  '四个月',
  '六个月',
  '八个月',
  '一年'
]

const COMMON_DURATION_MAP = {
  7: '一周',
  30: '一个月',
  365: '一年'
}

let goodsRowId = 0

function createDefaultPayMethod() {
  return {
    id: ++goodsRowId,
    payMethod: 0,
    provider: '',
    isDefault: true
  }
}

function createExtraPayMethod() {
  return {
    id: ++goodsRowId,
    payMethod: '',
    provider: '',
    isDefault: false
  }
}

function createSubscriptionGoods() {
  return {
    id: ++goodsRowId,
    goodsType: 1,
    goodsId: null,
    onlyThird: false,
    defaultPayMethod: '',
    defaultProvider: '',
    visible: false,
    previewGoodsId: null
  }
}

function createGoldGoods() {
  return {
    id: ++goodsRowId,
    isLocal: true,
    goodsType: 0,
    goodsId: null,
    visible: false,
    previewGoodsId: null
  }
}

function createGoodsSections(supportsGold = true) {
  return supportsGold
    ? [{ key: 'subscription' }, { key: 'gold' }]
    : [{ key: 'subscription' }]
}

function normalizeGoodsSections(sections, supportsGold = true) {
  const supportedKeys = supportsGold ? ['subscription', 'gold'] : ['subscription']
  const normalized = []
  const appendKey = (key) => {
    if (supportedKeys.indexOf(key) === -1) return
    if (normalized.some((item) => item.key === key)) return
    normalized.push({ key })
  }
  if (Array.isArray(sections)) {
    sections.forEach((section) => {
      appendKey(typeof section === 'string' ? section : section && section.key)
    })
  }
  supportedKeys.forEach(appendKey)
  return normalized
}

function createPanel(key, title, enabled, supportsGoods, supportsGold) {
  return {
    key,
    title,
    supportsGoods,
    supportsGold,
    thirdPay: enabled,
    payLinkType: 0,
    payMethods: [createDefaultPayMethod()],
    goodsSections: supportsGoods ? createGoodsSections(supportsGold) : [],
    subscriptionGoods: [],
    goldGoods: [],
    adTagGoodsId: null,
    adTagVisible: false,
    adTagPreviewGoodsId: null
  }
}

function createPlatformConfig(enabled) {
  return {
    panels: [
      createPanel('paywall', '付费墙', enabled, true, true),
      createPanel('wallet', '钱包页', enabled, true, true),
      createPanel('vip', 'VIP页', false, true, false)
    ],
    activity: {
      thirdPay: enabled,
      pushRuleIds: [],
      payMethods: [createDefaultPayMethod()]
    },
    discount: {
      thirdPay: enabled,
      payMethods: [createDefaultPayMethod()]
    }
  }
}

function normalizeOptionalNumber(value) {
  return value === 0 || (value !== null && value !== undefined && value !== '')
    ? Number(value)
    : ''
}

function normalizeGoodsId(value) {
  if (value === 0 || value === '0') return 0
  if (value === null || value === undefined || value === '') return null
  const numberValue = Number(value)
  return Number.isNaN(numberValue) ? value : numberValue
}

function normalizePayMethods(payMethods = []) {
  const list = Array.isArray(payMethods) && payMethods.length
    ? [...payMethods].sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
    : [createDefaultPayMethod()]
  const normalized = list.map((item) => {
    const payMethod = normalizeOptionalNumber(item.payMethod)
    return {
      id: ++goodsRowId,
      payMethod,
      provider: payMethod === 0 ? '' : normalizeOptionalNumber(item.provider),
      isDefault: !!item.isDefault,
      sortOrder: item.sortOrder
    }
  })
  if (!normalized.some((item) => item.isDefault) && normalized.length) {
    normalized[0].isDefault = true
  }
  return normalized
}

function normalizeSubscriptionGoods(list = []) {
  const source = Array.isArray(list) ? list : []
  return source.map((item) => ({
    id: ++goodsRowId,
    goodsType: Number(item.goodsType) === 2 ? 2 : 1,
    goodsId: normalizeGoodsId(item.goodsId || item.id),
    onlyThird: !!(item.onlyThirdParty || item.onlyThird),
    defaultPayMethod: normalizeOptionalNumber(item.defaultPayMethod),
    defaultProvider: normalizeOptionalNumber(item.defaultProvider),
    visible: false,
    previewGoodsId: normalizeGoodsId(item.previewGoodsId || item.goodsId)
  }))
}

function normalizeGoldGoods(list = []) {
  const source = Array.isArray(list) ? list : []
  return source.map((item) => ({
    id: ++goodsRowId,
    goodsType: 0,
    goodsId: normalizeGoodsId(item.goodsId || item.goods_id || (item.isLocal ? null : item.id)),
    visible: false,
    previewGoodsId: normalizeGoodsId(item.previewGoodsId || item.preview_goods_id || item.goodsId || item.goods_id)
  }))
}

function inferGoodsSections(payItems = [], supportsGold = true) {
  const items = Array.isArray(payItems) ? payItems : []
  const hasSubscription = items.some((item) => Number(item.goodsType) !== 0)
  const hasGold = supportsGold && items.some((item) => Number(item.goodsType) === 0)
  if (hasSubscription && hasGold) {
    return [{ key: 'subscription' }, { key: 'gold' }]
  }
  if (hasGold) return [{ key: 'gold' }]
  return [{ key: 'subscription' }]
}

function normalizePanel(source = {}, fallback = {}) {
  const supportsGoods =
    source.supportsGoods !== undefined ? !!source.supportsGoods : !!fallback.supportsGoods
  const supportsGold =
    source.supportsGold !== undefined ? !!source.supportsGold : !!fallback.supportsGold
  return {
    key: source.key || fallback.key,
    title: source.title || fallback.title,
    supportsGoods,
    supportsGold,
    thirdPay:
      source.thirdPay !== undefined ? !!source.thirdPay : !!fallback.thirdPay,
    payLinkType: Number(source.payLinkType || 0),
    payMethods: normalizePayMethods(source.payMethods || fallback.payMethods),
    goodsSections: supportsGoods
      ? normalizeGoodsSections(source.goodsSections || fallback.goodsSections, supportsGold)
      : [],
    subscriptionGoods: supportsGoods
      ? normalizeSubscriptionGoods(source.subscriptionGoods || fallback.subscriptionGoods)
      : [],
    goldGoods: supportsGold
      ? normalizeGoldGoods(source.goldGoods || fallback.goldGoods)
      : [],
    adTagGoodsId: normalizeGoodsId(source.adTagGoodsId),
    adTagVisible: false,
    adTagPreviewGoodsId: normalizeGoodsId(
      source.adTagPreviewGoodsId || source.adTagGoodsId
    )
  }
}

function normalizePlatformConfig(source = {}, enabled) {
  const fallback = createPlatformConfig(enabled)
  const sourcePanels = Array.isArray(source.panels) ? source.panels : []
  return {
    panels: fallback.panels.map((panel) => {
      const sourcePanel = sourcePanels.find((item) => item.key === panel.key)
      return normalizePanel(sourcePanel, panel)
    }),
    activity: {
      ...fallback.activity,
      ...(source.activity || {}),
      pushRuleIds: (source.activity && source.activity.pushRuleIds) || fallback.activity.pushRuleIds || [],
      payMethods: normalizePayMethods(
        (source.activity && source.activity.payMethods) || fallback.activity.payMethods
      )
    },
    discount: {
      ...fallback.discount,
      ...(source.discount || {}),
      payMethods: normalizePayMethods(
        (source.discount && source.discount.payMethods) || fallback.discount.payMethods
      )
    }
  }
}

export default {
  name: 'RechargeTemplateConfig',
  components: {
    draggable
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    experimentGroupCount: {
      type: Number,
      default: 1
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      controlPlatform: 'ios',
      experimentPlatforms: {},
      maxMethodCount: 6,
      activityOptions: [],
      iosMethodOptions: [
        { dictValue: '0', dictName: '内购' },
        { dictValue: '1', dictName: '卡支付' },
        { dictValue: '2', dictName: 'Apple Pay' },
        { dictValue: '4', dictName: '钱包' }
      ],
      androidMethodOptions: [
        { dictValue: '0', dictName: '内购' },
        { dictValue: '1', dictName: '卡支付' },
        { dictValue: '3', dictName: 'Google Pay' },
        { dictValue: '4', dictName: '钱包' }
      ],
      providerOptions: [
        { dictValue: '0', dictName: 'Stripe' },
        { dictValue: '1', dictName: 'Antom' },
        { dictValue: '2', dictName: 'PayPal' }
      ],
      goodsOptions: {
        ios: {
          vip: [],
          pass: [],
          gold: []
        },
        android: {
          vip: [],
          pass: [],
          gold: []
        }
      },
      templateOptions: [],
      templateSearchLoading: false,
      templateDetailLoading: false,
      templateSearchSeq: 0,
      selectedControlTemplateOption: null,
      selectedExperimentTemplateOptions: {},
      payRowId: 0
    }
  },
  computed: {
    groupItems() {
      const groups = Array.isArray(this.formData.groups) ? this.formData.groups : []
      return [
        {
          key: 'control',
          title: this.formData.controlGroupName || '对照组',
          config: this.formData.controlGroupParam
        },
        ...groups.map((group, index) => ({
          key: 'experiment-' + index,
          title: group.experimentGroupName || ('实验组 ' + (index + 1)),
          config: group.experimentGroupParam
        }))
      ]
    },
    controlCurrentConfig() {
      if (!this.formData.controlGroupParam) {
        this.$set(this.formData, 'controlGroupParam', normalizeGroupParamByCapability('10', {}))
      }
      return this.formData.controlGroupParam[this.controlPlatform]
    }
  },
  mounted() {
    this.fetchActivityOptions()
    this.loadGoodsOptions()
  },
  methods: {
    setTemplateOptions(options = []) {
      this.templateOptions = options.map(item => ({
        value: item.id,
        label: item.name,
        ...item
      }))
    },
    searchTemplateOptions: debounce(async function (query = '') {
      const keyword = String(query || '').trim()
      if (!keyword) {
        const selectedOptions = []
        if (this.selectedControlTemplateOption) {
          selectedOptions.push(this.selectedControlTemplateOption)
        }
        Object.values(this.selectedExperimentTemplateOptions).forEach(option => {
          if (option && !selectedOptions.some(item => item.value === option.value)) {
            selectedOptions.push(option)
          }
        })
        this.templateOptions = selectedOptions
        return
      }

      const searchSeq = ++this.templateSearchSeq
      this.templateSearchLoading = true
      try {
        const response = await getRechargeTemplateConfigPage({
          current: 1,
          size: 10000,
          name: keyword
        })
        if (searchSeq !== this.templateSearchSeq) return
        this.setTemplateOptions(response.data && response.data.records || [])
      } catch (error) {
        if (searchSeq === this.templateSearchSeq) {
          const selectedOptions = []
          if (this.selectedControlTemplateOption) {
            selectedOptions.push(this.selectedControlTemplateOption)
          }
          Object.values(this.selectedExperimentTemplateOptions).forEach(option => {
            if (option && !selectedOptions.some(item => item.value === option.value)) {
              selectedOptions.push(option)
            }
          })
          this.templateOptions = selectedOptions
        }
        this.$Message.error('搜索模板失败')
      } finally {
        if (searchSeq === this.templateSearchSeq) {
          this.templateSearchLoading = false
        }
      }
    }, 500),
    applyTemplateConfig(targetConfig, detail = {}) {
      const source = detail && (detail.rechargeConfig || detail.config || detail) || {}
      console.log('=== applyTemplateConfig ===')
      console.log('source:', source)
      console.log('source.iosRules:', source.iosRules)
      console.log('source.androidRules:', source.androidRules)
      console.log('source.ios:', source.ios)
      console.log('source.android:', source.android)
      
      const iosConfig = source.iosRules ? this.normalizeRulesPlatformConfig(source.iosRules, true) : this.normalizePlatformConfig(source.ios, true)
      const androidConfig = source.androidRules ? this.normalizeRulesPlatformConfig(source.androidRules, true) : this.normalizePlatformConfig(source.android, true)
      
      console.log('转换后的 iosConfig:', iosConfig)
      console.log('转换后的 androidConfig:', androidConfig)

      this.$set(targetConfig, 'ios', iosConfig)
      this.$set(targetConfig, 'android', androidConfig)
      
      console.log('设置后的 targetConfig:', targetConfig)
    },
    normalizeRulesPlatformConfig(rules = [], enabled) {
      const config = createPlatformConfig(enabled)
      const rulesArray = Array.isArray(rules) ? rules : []
      
      // 按 entryId 分组：0=付费墙, 1=钱包页, 2=VIP页, 3=折扣, 4=活动
      const entryMap = {}
      rulesArray.forEach(rule => {
        entryMap[rule.entryId] = rule
      })

      // 转换 panels (entryId: 0, 1, 2)
      config.panels = config.panels.map(panel => {
        const entryId = PANEL_ENTRY_MAP[panel.key]
        const rule = entryMap[entryId]
        if (!rule) return panel

        const payItems = rule.payItems || []
        const subscriptionGoods = payItems.filter(item => Number(item.goodsType) !== 0)
        // 金币付费项可能来自 coinsList 字段，或者 payItems 中 goodsType === 0 的项
        const coinsListGoldGoods = Array.isArray(rule.coinsList) ? rule.coinsList : []
        const payItemsGoldGoods = payItems.filter(item => Number(item.goodsType) === 0)
        const goldGoods = coinsListGoldGoods.length > 0 ? coinsListGoldGoods : payItemsGoldGoods
        const adUnlockItem = rule.adUnlockItem

        // 合并 payItems 和 coinsList 用于推断 goodsSections
        const allGoodsItems = [...payItems, ...coinsListGoldGoods.map(item => ({ ...item, goodsType: 0 }))]

        return {
          key: panel.key,
          title: panel.title,
          supportsGoods: panel.supportsGoods,
          supportsGold: panel.supportsGold,
          thirdPay: !!rule.switchOn,
          payLinkType: Number(rule.payLinkType || 0),
          payMethods: normalizePayMethods(rule.payMethods || panel.payMethods),
          goodsSections: panel.supportsGoods 
            ? inferGoodsSections(allGoodsItems, panel.supportsGold) 
            : panel.goodsSections,
          subscriptionGoods: panel.supportsGoods 
            ? normalizeSubscriptionGoods(subscriptionGoods) 
            : [],
          goldGoods: panel.supportsGold 
            ? normalizeGoldGoods(goldGoods) 
            : [],
          adTagGoodsId: adUnlockItem ? normalizeGoodsId(adUnlockItem.goodsId) : null,
          adTagVisible: !!adUnlockItem,
          adTagPreviewGoodsId: adUnlockItem ? normalizeGoodsId(adUnlockItem.goodsId) : null
        }
      })

      // 转换 activity (entryId: 4)
      const activityRule = entryMap[ACTIVITY_ENTRY_ID]
      if (activityRule) {
        config.activity.thirdPay = !!activityRule.switchOn
        config.activity.payLinkType = Number(activityRule.payLinkType || 0)
        config.activity.payMethods = normalizePayMethods(activityRule.payMethods || config.activity.payMethods)
        config.activity.pushRuleIds = activityRule.pushRuleIds || []
      }

      // 转换 discount (entryId: 3)
      const discountRule = entryMap[DISCOUNT_ENTRY_ID]
      if (discountRule) {
        config.discount.thirdPay = !!discountRule.switchOn
        config.discount.payLinkType = Number(discountRule.payLinkType || 0)
        config.discount.payMethods = normalizePayMethods(discountRule.payMethods || config.discount.payMethods)
      }

      return config
    },
    normalizePlatformConfig(source = {}, enabled) {
      return normalizePlatformConfig(source, enabled)
    },
    createPlatformConfig(enabled) {
      return {
        panels: [
          this.createPanel('paywall', '付费墙', enabled, true, true),
          this.createPanel('wallet', '钱包页', enabled, true, true),
          this.createPanel('vip', 'VIP页', false, true, false)
        ],
        activity: {
          thirdPay: enabled,
          pushRuleIds: [],
          payMethods: [this.createDefaultPayMethod()]
        },
        discount: {
          thirdPay: enabled,
          payMethods: [this.createDefaultPayMethod()]
        }
      }
    },
    createPanel(key, title, enabled, supportsGoods, supportsGold) {
      return {
        key,
        title,
        supportsGoods,
        supportsGold,
        thirdPay: enabled,
        payLinkType: 0,
        payMethods: [this.createDefaultPayMethod()],
        goodsSections: supportsGoods ? this.createGoodsSections(supportsGold) : [],
        subscriptionGoods: [],
        goldGoods: [],
        adTagGoodsId: null,
        adTagVisible: false,
        adTagPreviewGoodsId: null
      }
    },
    createGoodsSections(supportsGold = true) {
      return supportsGold ? [{ key: 'subscription' }, { key: 'gold' }] : [{ key: 'subscription' }]
    },
    createDefaultPayMethod() {
      return {
        id: ++this.payRowId,
        payMethod: 0,
        provider: '',
        isDefault: true
      }
    },
    async handleControlTemplateChange(value) {
      if (!value) {
        this.selectedControlTemplateOption = null
        this.templateOptions = []
        return
      }

      const selectedOption = this.templateOptions.find(
        (item) => String(item.value) === String(value)
      )
      if (selectedOption) this.selectedControlTemplateOption = selectedOption

      this.templateDetailLoading = true
      try {
        const { data } = await getRechargeTemplateConfigInfo(value)
        const detail = data || {}
        this.selectedControlTemplateOption = {
          value: detail.id,
          label: detail.name,
          ...detail
        }
        this.templateOptions = [this.selectedControlTemplateOption]
        if (!this.formData.controlGroupParam) {
          this.$set(this.formData, 'controlGroupParam', normalizeGroupParamByCapability('10', {}))
        }
        this.applyTemplateConfig(this.formData.controlGroupParam, detail)
      } catch (error) {
        this.$Message.error('获取模板详情失败')
      } finally {
        this.templateDetailLoading = false
      }
    },
    async handleExperimentTemplateChange(groupIndex) {
      const groups = Array.isArray(this.formData.groups) ? this.formData.groups : []
      const group = groups[groupIndex]
      if (!group || !group.experimentTemplateId) {
        this.selectedExperimentTemplateOptions[groupIndex] = null
        return
      }

      const value = group.experimentTemplateId
      const selectedOption = this.templateOptions.find(
        (item) => String(item.value) === String(value)
      )
      if (selectedOption) this.selectedExperimentTemplateOptions[groupIndex] = selectedOption

      this.templateDetailLoading = true
      try {
        const { data } = await getRechargeTemplateConfigInfo(value)
        const detail = data || {}
        this.selectedExperimentTemplateOptions[groupIndex] = {
          value: detail.id,
          label: detail.name,
          ...detail
        }
        this.templateOptions = Object.values(this.selectedExperimentTemplateOptions).filter(Boolean)
        if (!group.experimentGroupParam) {
          this.$set(group, 'experimentGroupParam', normalizeGroupParamByCapability('10', {}))
        }
        this.applyTemplateConfig(group.experimentGroupParam, detail)
      } catch (error) {
        this.$Message.error('获取模板详情失败')
      } finally {
        this.templateDetailLoading = false
      }
    },
    getControlConfig(platform) {
      if (!this.formData.controlGroupParam) {
        this.$set(this.formData, 'controlGroupParam', normalizeGroupParamByCapability('10', {}))
      }
      return this.formData.controlGroupParam[platform || this.controlPlatform]
    },
    getExperimentPlatform(index) {
      return this.experimentPlatforms[index] || 'ios'
    },
    setExperimentPlatform(index, platform) {
      this.$set(this.experimentPlatforms, index, platform)
    },
    getExperimentGroupConfig(index) {
      const platform = this.getExperimentPlatform(index)
      const groups = Array.isArray(this.formData.groups) ? this.formData.groups : []
      const experimentGroup = groups[index]
      if (!experimentGroup) return normalizePlatformConfig({}, false)
      if (!experimentGroup.experimentGroupParam) {
        this.$set(experimentGroup, 'experimentGroupParam', normalizeGroupParamByCapability('10', {}))
      }
      return experimentGroup.experimentGroupParam[platform]
    },
    getExperimentGroupTitle(index) {
      const groups = Array.isArray(this.formData.groups) ? this.formData.groups : []
      const group = groups[index]
      return group ? (group.experimentGroupName || ('实验组 ' + (index + 1))) : ('实验组 ' + (index + 1))
    },
    buildGoodsClassificationParams(platform) {
      return {
        channel: PLATFORM_CHANNEL_MAP[platform],
        goodsVersion: 1
      }
    },
    getClassificationData(response) {
      const data = response && response.data
      return data && !Array.isArray(data) ? data : {}
    },
    normalizeClassificationGoodsList(list = [], goodsType) {
      const source = Array.isArray(list) ? list : []
      return source.map((item) => ({
        ...item,
        id: normalizeGoodsId(item.id),
        goodsType:
          item.goodsType === undefined || item.goodsType === null
            ? goodsType
            : Number(item.goodsType)
      }))
    },
    async loadGoodsOptions() {
      try {
        const [iosClassification, androidClassification] = await Promise.all([
          getGoodsClassification(this.buildGoodsClassificationParams('ios')),
          getGoodsClassification(this.buildGoodsClassificationParams('android'))
        ])
        const iosGoods = this.getClassificationData(iosClassification)
        const androidGoods = this.getClassificationData(androidClassification)
        this.goodsOptions = {
          ios: {
            vip: this.normalizeClassificationGoodsList(iosGoods.vipList, 1),
            pass: this.normalizeClassificationGoodsList(iosGoods.passList, 2),
            gold: this.normalizeClassificationGoodsList(iosGoods.coinsList, 0)
          },
          android: {
            vip: this.normalizeClassificationGoodsList(androidGoods.vipList, 1),
            pass: this.normalizeClassificationGoodsList(androidGoods.passList, 2),
            gold: this.normalizeClassificationGoodsList(androidGoods.coinsList, 0)
          }
        }
      } catch (error) {
        this.$Message.error('获取付费项列表失败')
      }
    },
    getResponseList(response) {
      const data = response && response.data
      if (Array.isArray(data)) return data
      return data && Array.isArray(data.records) ? data.records : []
    },
    normalizeActivityPushRuleOption(item = {}) {
      const label = item.ruleName || item.activityName || item.name || ''
      return {
        id: item.id,
        label: label || String(item.id || ''),
        raw: item
      }
    },
    async fetchActivityOptions() {
      try {
        const response = await getActivityPushRuleList({
          current: 1,
          size: 10000
        })
        this.activityOptions = this.getResponseList(response)
          .filter((item) => item && item.id !== undefined && item.id !== null)
          .map((item) => this.normalizeActivityPushRuleOption(item))
      } catch (error) {
        this.activityOptions = []
        this.$Message.error('获取活动推送配置列表失败')
      }
    },
    getMethodOptions(platform) {
      return platform === 'ios' ? this.iosMethodOptions : this.androidMethodOptions
    },
    getFilteredProviders(holder, index) {
      const row = (holder && holder.payMethods && holder.payMethods[index]) || {}
      if (row.payMethod === 4) {
        return this.providerOptions.filter((item) => item.dictName === 'PayPal')
      }
      if (row.payMethod === 2 || row.payMethod === 3) {
        return this.providerOptions.filter((item) => item.dictName !== 'PayPal')
      }
      return this.providerOptions
    },
    getDefaultProviderOptions(payMethod) {
      if (payMethod === 4) {
        return this.providerOptions.filter((item) => item.dictName === 'PayPal')
      }
      if (payMethod === 2 || payMethod === 3) {
        return this.providerOptions.filter((item) => item.dictName !== 'PayPal')
      }
      return this.providerOptions
    },
    isWalletSelected(holder, index) {
      const row = (holder && holder.payMethods && holder.payMethods[index]) || {}
      return row.payMethod === 4
    },
    isAppleOrGooglePaySelected(holder, index) {
      const row = (holder && holder.payMethods && holder.payMethods[index]) || {}
      return row.payMethod === 2 || row.payMethod === 3
    },
    isStepThirdPay(panel) {
      return panel.thirdPay && panel.payLinkType === 1
    },
    isMethodUsed(holder, methodValue, currentIndex) {
      const list = (holder && holder.payMethods) || []
      return list.some(
        (row, idx) => idx !== currentIndex && Number(row.payMethod) === Number(methodValue)
      )
    },
    addPayMethod(holder) {
      if (!holder) return
      if (!Array.isArray(holder.payMethods)) {
        this.$set(holder, 'payMethods', [createDefaultPayMethod()])
        return
      }
      if (holder.payMethods.length >= this.maxMethodCount) return
      holder.payMethods.push(createExtraPayMethod())
    },
    removePayMethod(holder, index) {
      if (!holder || !Array.isArray(holder.payMethods)) return
      if (holder.payMethods.length <= 1) return
      const row = holder.payMethods[index]
      if (row && row.isDefault && holder.payMethods.length > 1) {
        const first = holder.payMethods.find((_, idx) => idx !== index)
        if (first) first.isDefault = true
      }
      holder.payMethods.splice(index, 1)
    },
    setDefaultPayMethod(holder, index) {
      if (!holder || !Array.isArray(holder.payMethods)) return
      holder.payMethods.forEach((row, idx) => {
        row.isDefault = idx === index
      })
    },
    onMethodChange(holder, index, value) {
      const row = holder && holder.payMethods && holder.payMethods[index]
      if (!row) return
      if (value === 0) {
        row.provider = ''
      } else {
        const providers = this.getFilteredProviders(holder, index)
        if (
          !row.provider ||
          !providers.some(
            (item) => Number(item.dictValue) === Number(row.provider)
          )
        ) {
          row.provider = providers.length
            ? Number(providers[0].dictValue)
            : ''
        }
      }
    },
    onDefaultPayMethodChange(row, value) {
      if (!row) return
      const providers = this.getDefaultProviderOptions(value)
      if (
        !row.defaultProvider ||
        !providers.some(
          (item) => Number(item.dictValue) === Number(row.defaultProvider)
        )
      ) {
        row.defaultProvider = providers.length
          ? Number(providers[0].dictValue)
          : ''
      }
    },
    getPanelGoodsSections(panel) {
      if (!panel || !panel.supportsGoods) return []
      const sections = normalizeGoodsSections(panel.goodsSections, panel.supportsGold)
      const current = Array.isArray(panel.goodsSections) ? panel.goodsSections : []
      const changed =
        sections.length !== current.length ||
        sections.some((section, index) => {
          return !current[index] || current[index].key !== section.key
        })
      if (changed) {
        this.$set(panel, 'goodsSections', sections)
        return sections
      }
      return panel.goodsSections
    },
    getGoodsOptions(platform, type) {
      return this.goodsOptions[platform] && this.goodsOptions[platform][type]
        ? this.goodsOptions[platform][type]
        : []
    },
    getSubscriptionOptions(platform, row) {
      return this.getGoodsOptions(platform, row.goodsType === 2 ? 'pass' : 'vip')
    },
    getAdTagGoodsOptions(platform) {
      return this.getGoodsOptions(platform, 'vip')
    },
    isSameGoodsId(left, right) {
      if (
        left === undefined ||
        left === null ||
        right === undefined ||
        right === null
      ) {
        return false
      }
      return String(left) === String(right)
    },
    hasSelectedGoods(item) {
      return !!(
        item &&
        (item.goodsId === 0 ||
          (item.goodsId !== undefined && item.goodsId !== null && item.goodsId !== ''))
      )
    },
    isGoodsUsed(list, goodsId, currentIndex) {
      return list.some((item, index) => {
        return (
          index !== currentIndex &&
          this.hasSelectedGoods(item) &&
          this.isSameGoodsId(item.goodsId, goodsId)
        )
      })
    },
    isSubscriptionGoodsUsed(list, goodsType, goodsId, currentIndex) {
      return list.some((item, index) => {
        return (
          index !== currentIndex &&
          Number(item.goodsType) === Number(goodsType) &&
          this.hasSelectedGoods(item) &&
          this.isSameGoodsId(item.goodsId, goodsId)
        )
      })
    },
    getGoodsById(platform, goodsType, goodsId) {
      const type = goodsType === 0 ? 'gold' : goodsType === 2 ? 'pass' : 'vip'
      return this.getGoodsOptions(platform, type).find(
        (item) => this.isSameGoodsId(item.id, goodsId)
      )
    },
    getPreviewGoods(row, platform) {
      return this.getGoodsById(
        platform,
        row.goodsType,
        row.previewGoodsId || row.goodsId
      )
    },
    getSubscriptionTipGoods(row, platform) {
      return this.getGoodsById(platform, row.goodsType, row.goodsId)
    },
    getGoldPickerPreview(row, platform) {
      return this.getGoodsById(platform, 0, row.previewGoodsId || row.goodsId)
    },
    getGoodsSelectLabel(row, platform) {
      const item = this.getGoodsById(platform, row.goodsType, row.goodsId)
      if (!item) return '— 选择付费项 —'
      return `${row.goodsType === 2 ? '通行证' : 'VIP'} - ${this.formatGoodsLabel(
        item
      )}`
    },
    getGoodsTriggerText(row, platform) {
      const item = this.getGoodsById(platform, row.goodsType, row.goodsId)
      return item ? item.goodsName : '— 选择付费项 —'
    },
    getGoldTriggerText(row, platform) {
      const item = this.getGoldPreview(row, platform)
      return item ? item.goodsName : '— 选择金币项 —'
    },
    getThirdMethodOptions(platform) {
      return this.getMethodOptions(platform).filter(
        (item) => Number(item.dictValue) !== 0
      )
    },
    getAdTagSelectedGoods(panel, platform) {
      return this.getGoodsById(platform, 1, panel.adTagGoodsId)
    },
    getAdTagPickerPreview(panel, platform) {
      return this.getGoodsById(
        platform,
        1,
        panel.adTagPreviewGoodsId || panel.adTagGoodsId
      )
    },
    getAdTagTriggerText(panel, platform) {
      const item = this.getAdTagSelectedGoods(panel, platform)
      return item ? item.goodsName : '— 选择订阅付费项 —'
    },
    getGoldPreview(row, platform) {
      return this.getGoodsById(platform, 0, row.goodsId)
    },
    formatGoodsLabel(item = {}) {
      const price =
        item.goodsPrice !== undefined && item.goodsPrice !== null
          ? item.goodsPrice
          : '--'
      const priceParts = []
      if (item.goodsChannelName) priceParts.push(item.goodsChannelName)
      priceParts.push(price)
      if (item.goodsNum !== undefined && item.goodsNum !== null) {
        priceParts.push(item.goodsNum)
      }
      return `${priceParts.join('-')}(${item.goodsName || '--'})`
    },
    formatGoodsPrice(item = {}) {
      if (item.goodsPrice === undefined || item.goodsPrice === null) return '--'
      return `$${item.goodsPrice}`
    },
    formatGoodsDuration(item = {}, platform) {
      if (item.vipExpireTime) {
        if (platform === 'android' && Number(item.goodsType) === 1) {
          return VIP_DURATION_MAP[item.vipExpireTime - 1] || item.vipExpireTime
        }
        return COMMON_DURATION_MAP[item.vipExpireTime] || `${item.vipExpireTime}天`
      }
      if (item.goodsDay) return `${item.goodsDay}天`
      if (item.goodsNum && item.goodsType === 2) return `${item.goodsNum}天`
      return '--'
    },
    formatGoldCount(item = {}) {
      if (item.goodsNum !== undefined && item.goodsNum !== null) {
        return item.goodsNum
      }
      return '--'
    },
    formatDiscountValue(config) {
      if (!config) return '--'
      if (typeof config !== 'object') return config
      if (config.price !== undefined && config.price !== null) {
        return `$${config.price}`
      }
      if (config.discount !== undefined && config.discount !== null) {
        return config.discount
      }
      return '--'
    },
    hasDisplayValue(value) {
      return value !== undefined && value !== null && value !== ''
    },
    hasRewardValue(value) {
      return this.hasDisplayValue(value) && Number(value) > 0
    },
    getRewardValue(item = {}, detailKey, fallbackKey) {
      const rewardDetail = item.rewardDetail || {}
      return this.hasDisplayValue(rewardDetail[detailKey])
        ? rewardDetail[detailKey]
        : item[fallbackKey]
    },
    getDiscountConfigFromPriceDetail(priceDetail, key) {
      if (
        !priceDetail ||
        priceDetail[key] === undefined ||
        priceDetail[key] === null
      ) {
        return null
      }
      return { price: priceDetail[key] }
    },
    getInnerDiscountValue(item = {}) {
      const config = this.getDiscountConfigFromPriceDetail(
        item.priceDetail,
        'iapPrice'
      )
      if (config) return this.formatDiscountValue(config)
      if (this.hasDisplayValue(item.discountGoodsPrice)) {
        return `$${item.discountGoodsPrice}`
      }
      if (this.hasDisplayValue(item.discount)) {
        return this.formatDiscountValue(item.discount)
      }
      return null
    },
    getRewardPreview(item = {}) {
      return [
        {
          key: 'reward-inner',
          label: '内购赠币',
          rawValue: this.getRewardValue(item, 'iapReward', 'iapReward'),
          className: 'green-text'
        },
        {
          key: 'reward-stripe',
          label: 'Stripe赠币',
          rawValue: this.getRewardValue(item, 'stripeReward', 'stripeReward'),
          className: 'blue-text'
        },
        {
          key: 'reward-antom',
          label: 'Antom赠币',
          rawValue: this.getRewardValue(item, 'antomReward', 'antomReward'),
          className: 'blue-text'
        },
        {
          key: 'reward-paypal',
          label: 'PayPal赠币',
          rawValue: this.getRewardValue(item, 'paypalReward', 'paypalReward'),
          className: 'blue-text'
        }
      ]
        .filter((row) => this.hasRewardValue(row.rawValue))
        .map((row) => ({
          ...row,
          value: row.rawValue
        }))
    },
    getDiscountPreview(item = {}) {
      const thirdMap = item.thirdPaymentDiscountMap || {}
      const priceDetail = item.priceDetail || {}
      return [
        {
          key: 'discount-inner',
          label: '内购折扣价',
          value: this.getInnerDiscountValue(item),
          className: 'green-text'
        },
        {
          key: 'discount-stripe',
          label: 'Stripe折扣价',
          value: this.formatDiscountValue(
            thirdMap.stripe ||
              this.getDiscountConfigFromPriceDetail(priceDetail, 'stripePrice')
          ),
          className: 'blue-text'
        },
        {
          key: 'discount-antom',
          label: 'Antom折扣价',
          value: this.formatDiscountValue(
            thirdMap.antom ||
              this.getDiscountConfigFromPriceDetail(priceDetail, 'antomPrice')
          ),
          className: 'blue-text'
        },
        {
          key: 'discount-paypal',
          label: 'PayPal折扣价',
          value: this.formatDiscountValue(
            thirdMap.paypal ||
              this.getDiscountConfigFromPriceDetail(priceDetail, 'paypalPrice')
          ),
          className: 'blue-text'
        }
      ].filter((row) => row.value !== '--' && this.hasDisplayValue(row.value))
    },
    getSubscriptionTipRows(item = {}, platform) {
      if (!item.id) return []
      const rows = [
        {
          key: 'duration',
          label: '时长',
          value: this.formatGoodsDuration(item, platform)
        },
        {
          key: 'inner-price',
          label: '内购价',
          value: this.formatGoodsPrice(item)
        }
      ]
      rows.push(...this.getDiscountPreview(item))
      if (Number(item.goodsType) === 2) {
        rows.push(...this.getRewardPreview(item))
      }
      return rows
    },
    getGoldTipRows(item = {}) {
      if (!item.id) return []
      return [
        {
          key: 'gold',
          label: '金币数',
          value: this.formatGoldCount(item)
        },
        {
          key: 'inner-price',
          label: '内购价',
          value: this.formatGoodsPrice(item)
        },
        ...this.getDiscountPreview(item),
        ...this.getRewardPreview(item)
      ]
    },
    setSubscriptionType(row, type) {
      if (!row) return
      row.goodsType = type
      row.goodsId = null
      row.previewGoodsId = null
    },
    setPreviewGoods(row, goodsId) {
      if (!row) return
      row.previewGoodsId = goodsId
    },
    setGoldPreviewGoods(row, goodsId) {
      if (!row) return
      row.previewGoodsId = goodsId
    },
    setAdTagPreviewGoods(panel, goodsId) {
      if (!panel) return
      panel.adTagPreviewGoodsId = goodsId
    },
    selectSubscriptionGoods(row, goods) {
      if (!row || !goods) return
      row.goodsId = goods.id
      row.previewGoodsId = goods.id
      row.visible = false
    },
    selectGoldGoods(row, goods) {
      if (!row || !goods) return
      row.goodsId = goods.id
      row.previewGoodsId = goods.id
      row.isLocal = false
      row.visible = false
    },
    selectAdTagGoods(panel, goods) {
      if (!panel || !goods) return
      panel.adTagGoodsId = goods.id
      panel.adTagPreviewGoodsId = goods.id
      panel.adTagVisible = false
    },
    clearAdTagGoods(panel) {
      if (!panel) return
      panel.adTagGoodsId = null
      panel.adTagPreviewGoodsId = null
    },
    addSubscriptionGoods(panel) {
      if (!panel) return
      if (!Array.isArray(panel.subscriptionGoods)) {
        this.$set(panel, 'subscriptionGoods', [])
      }
      panel.subscriptionGoods.push(createSubscriptionGoods())
    },
    addGoldGoods(panel) {
      if (!panel) return
      if (!Array.isArray(panel.goldGoods)) {
        this.$set(panel, 'goldGoods', [])
      }
      panel.goldGoods.push(createGoldGoods())
    },
    removeGoldGoodsRow(panel, index) {
      if (!panel || !Array.isArray(panel.goldGoods)) return
      if (panel.goldGoods.length <= 1) return
      panel.goldGoods.splice(index, 1)
    },
    removeGoodsRow(list, index) {
      if (!Array.isArray(list)) return
      if (list.length <= 1) return
      list.splice(index, 1)
    }
  }
}
</script>

<style scoped lang="less">
.recharge-template-config {
  width: 100%;
  background: #fff;
}

.group-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 12px;
}

.group-card {
  padding: 12px;
  background: #fafbfc;
  border: 1px solid #e8eaec;
  border-radius: 6px;
}

.group-title {
  margin: 0 0 8px;
  padding: 8px 12px;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  background: linear-gradient(90deg, #e6f7ff 0%, #fff 100%);
  border-left: 3px solid #2d8cf0;
  border-radius: 4px;
}

.section-title {
  margin: 12px 0 10px;
  color: #333;
  font-size: 14px;
  font-weight: 600;
}

.payment-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(220px, 1fr));
  gap: 10px;
}

.payment-card {
  min-height: 250px;
  padding: 0 10px 10px;
  border: 1px solid #e8eaec;
  border-radius: 4px;
  background: #fff;
}

.payment-title {
  height: 32px;
  margin: 0 -10px 8px;
  padding: 8px 12px;
  color: #333;
  font-size: 12px;
  font-weight: 600;
  background: #fbfbfc;
  border-bottom: 1px solid #e8eaec;
}

.switch-box {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 8px;
  color: #333;
  font-size: 12px;
  background: #fafafa;
  border: 1px solid #e8eaec;
  border-radius: 4px;
}

.radio-row {
  display: flex;
  align-items: center;
  height: 30px;
  font-size: 12px;
}

.table-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  color: #333;
  font-size: 12px;
}

.method-table,
.simple-table {
  margin-top: 4px;
  border: 1px solid #edf0f2;
}

.method-head,
.method-row {
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) minmax(0, 1fr) 40px 20px;
  align-items: center;
  min-height: 32px;
  column-gap: 6px;
  padding: 0 8px;
}

.method-head {
  color: #666;
  font-size: 12px;
  background: #fafafa;
  border-bottom: 1px solid #edf0f2;

  span:nth-child(4) {
    text-align: center;
  }
}

.method-row {
  padding-top: 3px;
  padding-bottom: 3px;
  border-bottom: 1px solid #f2f3f5;

  &:last-child {
    border-bottom: 0;
  }

  /deep/ .ivu-select {
    width: 100%;
  }

  /deep/ .ivu-radio-wrapper {
    margin-right: 0;
    justify-self: center;
  }

  .row-delete {
    justify-self: center;
  }
}

.simple-table {
  .method-head,
  .method-row {
    grid-template-columns: 36px minmax(120px, 1fr) minmax(120px, 1fr) 60px 24px;
  }
}

.drag-handle {
  color: #bbb;
  text-align: center;
  cursor: move;
}

.row-delete {
  color: #ff4d4f;
  text-align: center;
  cursor: pointer;
}

.add-row {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  margin-top: 6px;
  padding: 0 8px;
  color: #999;
  font-size: 12px;
  background: #fff;
  border: 1px dashed #edf0f2;
  border-radius: 2px;
  cursor: pointer;
}

.goods-config-table {
  margin-top: 8px;
  position: relative;
}

.goods-group-title {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  margin-top: 8px;
  padding: 0 8px;
  color: #333;
  font-size: 12px;
  font-weight: 600;
  background: #fafafa;
  border: 1px solid #edf0f2;
  border-radius: 2px;
}

.group-handle {
  font-weight: 400;
}

.subscription-item {
  margin-top: 6px;
  padding: 8px;
  border: 1px solid #edf0f2;
  border-radius: 4px;
  background: #fff;
}

.subscription-main {
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr) 64px 16px;
  align-items: center;
  column-gap: 8px;

  /deep/ .ivu-select {
    width: 100%;
  }

  .row-delete {
    justify-self: center;
  }

  &.without-third {
    grid-template-columns: 16px minmax(0, 1fr) 16px;
  }
}

.subscription-default {
  margin-top: 8px;
  padding: 10px;
  background: #fafafa;
  border-radius: 4px;
}

.step-pay-tip {
  padding: 6px 8px;
  color: #808695;
  font-size: 12px;
  line-height: 18px;
  background: #f7fbfa;
  border: 1px solid #dcdee2;
  border-radius: 4px;
}

.default-line {
  display: flex;
  align-items: center;
  font-size: 12px;

  label {
    width: 84px;
    color: #515a6e;
    flex-shrink: 0;
  }

  .inline-label {
    width: auto;
    margin-left: 16px;
  }

  /deep/ .ivu-select {
    width: 120px;
  }
}

.gold-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 6px;
}

.gold-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  column-gap: 6px;

  /deep/ .ivu-select {
    width: 100%;
  }

  .row-delete {
    justify-self: center;
  }
}

.goods-picker-poptip {
  width: 100%;
  position: relative;

  /deep/ .ivu-poptip-rel {
    width: 100%;
  }

  /deep/ .ivu-poptip-popper {
    position: absolute !important;
  }
}

.goods-picker-trigger-wrap {
  position: relative;
  width: 100%;
}

.goods-mini-preview {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  z-index: 20;
  display: none;
  width: 200px;
  padding: 10px 12px;
  color: #515a6e;
  font-size: 12px;
  white-space: normal;
  pointer-events: none;
  background: #fff;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform: translateX(-50%);
}

.goods-picker-trigger-wrap:hover .goods-mini-preview {
  display: block;
}

.goods-mini-title {
  margin-bottom: 6px;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
}

.goods-mini-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 22px;
  color: #999;

  strong {
    min-width: 56px;
    color: #515a6e;
    text-align: right;
    word-break: break-all;
  }
}

.goods-picker-trigger {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 24px 0 8px;
  color: #515a6e;
  white-space: nowrap;
  overflow: hidden;
  background: #fff;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  cursor: pointer;

  .trigger-text {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 8px;
    width: 0;
    height: 0;
    margin-top: -2px;
    border: 4px solid transparent;
    border-top-color: #808695;
  }

  &.disabled {
    color: #c5c8ce;
    background: #f3f3f3;
    cursor: not-allowed;
  }
}

.subscription-picker {
  width: 560px;
  color: #515a6e;
  white-space: normal;
}

.picker-tabs {
  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border-bottom: 1px solid #edf0f2;

  span {
    height: 32px;
    margin-right: 22px;
    color: #515a6e;
    font-weight: 600;
    line-height: 32px;
    cursor: pointer;
    border-bottom: 2px solid transparent;
  }

  .active {
    color: #2d8cf0;
    border-bottom-color: #2d8cf0;
  }
}

.picker-content {
  display: grid;
  grid-template-columns: minmax(0, 280px) minmax(0, 1fr);
  height: 250px;
  overflow: hidden;
}

.picker-list {
  min-height: 0;
  padding: 10px 12px;
  overflow-x: hidden;
  overflow-y: auto;
  border-right: 1px solid #edf0f2;
}

.picker-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 3px;

  &:hover,
  &.selected {
    background: #f3f9ff;
  }

  &.disabled {
    color: #c5c8ce;
    cursor: not-allowed;
    background: #fff;
  }
}

.picker-name {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.picker-price {
  color: #999;
  font-weight: 600;
}

.picker-preview {
  min-width: 0;
  min-height: 0;
  padding: 18px 16px;
  overflow-x: hidden;
  overflow-y: auto;
}

.preview-title {
  margin-bottom: 12px;
  color: #333;
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
}

.preview-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 28px;
  color: #999;
  font-size: 12px;

  strong {
    min-width: 72px;
    color: #515a6e;
    text-align: right;
    word-break: break-all;
  }
}

.empty-text {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: #999;
  font-size: 12px;
}

.ad-tag-box {
  margin-top: 8px;
  padding: 8px;
  color: #2d8cf0;
  font-size: 12px;
  background: #e6f7ff;
  border: 1px dashed #91d5ff;
  border-radius: 4px;
}

.ad-tag-title {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 16px;
}

.ad-tag-picker-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  column-gap: 6px;
  margin-top: 6px;

  .row-delete {
    justify-self: center;
  }
}

.inner-purchase-tag {
  display: inline-flex;
  align-items: center;
  height: 14px;
  padding: 0 4px;
  color: #fff !important;
  font-size: 10px;
  line-height: 14px;
  background: #2d8cf0 !important;
  border-color: #2d8cf0 !important;
  border-radius: 2px;
}

.wide-section {
  margin-top: 18px;
}

.wide-card {
  padding: 12px;
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 4px;
}

.activity-content {
  margin-top: 8px;
}

.activity-row {
  display: grid;
  grid-template-columns: 72px minmax(260px, 1fr);
  align-items: center;
  margin-bottom: 8px;
  color: #333;
  font-size: 12px;
  position: relative;
  z-index: 1;

  /deep/ .ivu-select {
    position: static;
  }

  /deep/ .ivu-select-dropdown {
    position: absolute !important;
  }
}

@media (max-width: 1400px) {
  .group-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 1200px) {
  .payment-grid {
    grid-template-columns: repeat(3, 280px);
    overflow-x: auto;
  }
}

.form-line {
  display: flex;
  align-items: center;
  min-height: 36px;
  margin-bottom: 16px;

  :deep(.ivu-form-item) {
    margin-right: 20px;
    margin-bottom: 0;
  }
}

.template-select {
  width: 180px;
}

.form-tip {
  color: #999;
  font-size: 12px;
}
</style>

<style lang="less">
.recharge-subscription-popper,
.recharge-gold-picker-popper {
  .ivu-poptip-inner {
    white-space: normal;
  }

  .ivu-poptip-body,
  .ivu-poptip-body-content {
    overflow: visible;
  }
}

.recharge-activity-popper {
  z-index: 1000 !important;
  position: absolute !important;
}
</style>